<script setup lang="ts">
import { storeToRefs } from 'pinia'

import { vClickOutside } from '~/directives/ClickOutside'

import RatioContainer from '~/components/shared/ratioContainer.vue'
import TrackThumbnailHolder from '~/components/track/thumbnailHolder.vue'
import TrackUniveralPlayer from '~/components/track/universalPlayer.vue'
import VCheckbox from '~/components/ui/Inputs/VCheckbox.vue'

import { useLanguage } from '~/composables/useLanguage'
import { useProvideCoreFetch } from '~/composables/useProvideCoreFetch'

import { useUserStore } from '~/stores/user'
import { useUserBandSetStore } from '~/stores/userBandSet'

import { formatDateToDefaultDisplay } from '~/utils/DateDisplay'

import { provideUpdateTrackInfo } from '~/api-core/Track/UpdateTrackInfo'

import type Band from '~/types/band'
import type Track from '~/types/track'

type Props = {
  modelValue: Track
  band: Band
}

type Emits = {
  'update:modelValue': [value: Track]
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const { coreFetch } = useProvideCoreFetch()
const { getCurrentLanguage } = useLanguage()

const { IS_ADMIN } = storeToRefs(useUserStore())
const { GET_BAND_INDEX_FROM_ID: HAS_BAND } = useUserBandSetStore()

const displayPlayer = ref(false)
const displayContextMenu = ref(false)
const loading = ref(false)

const isOwner = computed(() => {
  return HAS_BAND(props.band.id) !== -1 || IS_ADMIN.value
})

async function togglePublicStatus() {
  if (loading.value) return

  const patchTrackRequest = provideUpdateTrackInfo(coreFetch)

  try {
    const resp = await patchTrackRequest(props.modelValue.id, {
      is_public: !props.modelValue.is_public,
    })
    emit('update:modelValue', resp)
  } catch (_) {
    // handle error
  }
}

async function toggleArchive() {
  if (loading.value) return

  const patchTrackRequest = provideUpdateTrackInfo(coreFetch)

  try {
    const resp = await patchTrackRequest(props.modelValue.id, {
      is_archived: !props.modelValue.is_archived,
    })
    emit('update:modelValue', resp)
  } catch (_) {
    // handle error
  }
}

function toggleContextMenu() {
  displayContextMenu.value = !displayContextMenu.value
}

function hideContextMenu() {
  displayContextMenu.value = false
}

function showPlayer() {
  displayPlayer.value = true
}

function hidePlayer() {
  displayPlayer.value = false
}
</script>

<template>
  <div
    class="trackDisplayWrapper"
    :class="{ archived: modelValue.is_archived }"
  >
    <div class="headWrap">
      <div class="ellipsis tw-text-h6">
        {{ modelValue.name }}
      </div>
      <div
        v-if="isOwner"
        v-click-outside="hideContextMenu"
        class="contextMenuOuterWrap"
      >
        <i class="fa-solid fa-ellipsis" @click="toggleContextMenu" />
        <div v-if="displayContextMenu" class="contextMenu">
          <div class="elem">
            <VCheckbox
              :model-value="!!modelValue.is_public"
              :label="$t('track.display00')"
              @update:model-value="togglePublicStatus"
            />
          </div>
          <div class="elem" @click="toggleArchive">
            <i class="fa-solid fa-inbox" />
            <div v-if="!modelValue.is_archived" class="text">
              {{ $t('track.display01') }}
            </div>
            <div v-else class="text">
              {{ $t('track.display02') }}
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-if="modelValue.release_date !== null" class="subtitle">
      <span>{{ $t('track.display03') }}</span>
      <span>{{
        formatDateToDefaultDisplay(
          modelValue.release_date,
          getCurrentLanguage(),
        )
      }}</span>
    </div>
    <div v-else class="subtitle" />
    <div v-click-outside="hidePlayer" class="preview" @click="showPlayer">
      <RatioContainer format="16:9">
        <Transition mode="out-in">
          <TrackThumbnailHolder
            v-if="!displayPlayer"
            key="0"
            :track-url="modelValue.link ?? ''"
          />
          <TrackUniveralPlayer v-else key="1" :link="modelValue.link" />
        </Transition>
      </RatioContainer>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.trackDisplayWrapper {
  position: relative;
  width: 100%;
}

.headWrap {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.fa-solid {
  font-size: 28px;
  font-weight: 700;
  cursor: pointer;
  color: var(--color-black);
  transition: color 0.15s var(--trans-standard);
}

.fa-solid:hover {
  color: var(--color-main);
  transition: color 0.1s var(--trans-decelerate);
}

.subtitle {
  margin-top: 8px;
  color: var(--color-masala-grey-h);
  font-size: var(--font-size-small);
  min-height: 21px;
}

.preview {
  cursor: pointer;
  margin-top: 24px;
  width: 100%;
  overflow: hidden;
  border-radius: 4px;
}

.contextMenu {
  position: absolute;
  right: 0px;
  top: 34px;
  box-shadow: 0px 1px 5px 0px rgba(43, 38, 36, 0.2);
  background-color: var(--color-white);
  z-index: 501;
}

.elem {
  padding: 8px 16px;
  border-top: 1px solid var(--color-dark-ebb-grey);
  transition: 0.15s var(--trans-standard);
  display: flex;
  justify-content: flex-start;
  align-items: center;
}

.elem :deep(img) {
  width: 18px;
  height: 18px;
  overflow: hidden;
  object-fit: contain;
  object-position: center;
  margin-right: 8px;
}

.elem:hover {
  background-color: var(--color-background);
  transition: 0.1s var(--trans-decelerate);
}

.elem:first-child {
  border-top-width: 0px;
}

.archived .preview {
  opacity: 0.5;
}
.archived * {
  color: rgba(0, 0, 0, 0.5);
}

.elem .fa-solid {
  font-size: 18px;
  color: var(--color-del-rio-grey);
  margin-right: 8px;
}

.contextMenuOuterWrap {
  display: flex;
  align-items: center;
  justify-content: center;
}

@media screen and (max-width: 720px) {
  .elem .fa-solid {
    font-size: 28px;
    margin-right: 16px;
  }
}
</style>
